import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings2 } from "lucide-react";
import { getCookieConsent, setCookieConsent, openCookiePreferences } from "../lib/cookieConsent";

/**
 * GDPR-compliant cookie consent banner.
 *
 * Two surfaces:
 *  - Bottom-fixed banner shown until the user makes a choice
 *  - "Customize" modal with per-category toggles
 *
 * Also renders the floating "Cookie preferences" pill anchored bottom-left
 * (visible only AFTER a choice has been made) so the user can change their
 * decision at any time (legally required by the EU ePrivacy directive).
 */
export default function CookieConsent() {
  const [decision, setDecision] = useState(() => getCookieConsent());
  const [customizing, setCustomizing] = useState(false);
  const [draft, setDraft] = useState({ analytics: false, marketing: false });

  // Listen to programmatic open events (footer link, preferences button)
  useEffect(() => {
    const handler = () => {
      const current = getCookieConsent();
      setDraft({
        analytics: !!current?.analytics,
        marketing: !!current?.marketing,
      });
      setCustomizing(true);
    };
    window.addEventListener("tnm-open-cookie-preferences", handler);
    return () => window.removeEventListener("tnm-open-cookie-preferences", handler);
  }, []);

  const persist = useCallback((prefs) => {
    const saved = setCookieConsent(prefs);
    setDecision(saved);
    setCustomizing(false);
  }, []);

  const acceptAll = () => persist({ analytics: true, marketing: true });
  const rejectAll = () => persist({ analytics: false, marketing: false });
  const saveCustom = () => persist(draft);

  const hasDecision = !!decision;

  return (
    <>
      {/* Main banner: shown until the user makes any choice */}
      {!hasDecision && !customizing && (
        <div
          className="fixed left-0 right-0 bottom-0 z-[100] bg-[#0A0A0A] border-t border-[#C5A059]/40 backdrop-blur-md"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div className="max-w-6xl mx-auto p-4 md:p-5 flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-[#C5A059] shrink-0 mt-0.5" />
              <div className="text-white/80 text-sm leading-relaxed">
                <p className="font-medium text-white mb-1">We value your privacy</p>
                <p>
                  We use cookies that are strictly necessary to run this site, plus optional cookies for analytics and marketing — only with your consent. Read our{" "}
                  <Link to="/legal/cookies" className="text-[#C5A059] hover:underline" data-testid="cookie-banner-policy-link">
                    Cookie Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-[#C5A059] hover:underline">
                    Privacy Policy
                  </Link>{" "}
                  for full details.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <button
                type="button"
                onClick={() => setCustomizing(true)}
                className="px-4 py-2 text-sm rounded-sm border border-white/15 text-white/80 hover:bg-white/5 inline-flex items-center justify-center gap-2"
                data-testid="cookie-customize-btn"
              >
                <Settings2 className="w-4 h-4" /> Customize
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="px-4 py-2 text-sm rounded-sm border border-white/15 text-white hover:bg-white/5"
                data-testid="cookie-reject-all-btn"
              >
                Reject all
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="px-4 py-2 text-sm rounded-sm bg-[#C5A059] text-black font-semibold hover:bg-[#d4b070]"
                data-testid="cookie-accept-all-btn"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating preferences pill — only after a decision exists */}
      {hasDecision && !customizing && (
        <button
          type="button"
          onClick={openCookiePreferences}
          className="fixed left-4 bottom-4 z-40 bg-[#0A0A0A]/90 backdrop-blur border border-white/15 text-white/70 hover:text-[#C5A059] hover:border-[#C5A059]/40 rounded-full p-2 shadow-lg transition-colors"
          aria-label="Open cookie preferences"
          title="Cookie preferences"
          data-testid="cookie-preferences-pill"
        >
          <Cookie className="w-4 h-4" />
        </button>
      )}

      {/* Customize modal */}
      {customizing && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => hasDecision && setCustomizing(false)}
          data-testid="cookie-customize-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#111] border border-white/10 rounded-sm w-full max-w-lg max-h-[85vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Cookie className="w-5 h-5 text-[#C5A059]" /> Cookie preferences
              </h3>
              {hasDecision && (
                <button
                  type="button"
                  onClick={() => setCustomizing(false)}
                  className="text-white/40 hover:text-white"
                  aria-label="Close"
                  data-testid="cookie-customize-close"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="p-5 space-y-4">
              <p className="text-white/60 text-xs">
                Choose which categories of cookies you want to allow. You can change this at any time from the cookie icon in the bottom-left corner.
              </p>

              <CategoryRow
                title="Strictly necessary"
                description="Required for the site to function (login session, security, your cookie-consent choice). These cannot be disabled."
                checked={true}
                disabled
                testId="cookie-cat-necessary"
              />
              <CategoryRow
                title="Analytics"
                description="Help us count visits and traffic sources so we can measure and improve site performance. Uses Google Analytics with IP anonymisation."
                checked={draft.analytics}
                onChange={(v) => setDraft((p) => ({ ...p, analytics: v }))}
                testId="cookie-cat-analytics"
              />
              <CategoryRow
                title="Marketing"
                description="Used to show you relevant ads on third-party sites and to measure their effectiveness."
                checked={draft.marketing}
                onChange={(v) => setDraft((p) => ({ ...p, marketing: v }))}
                testId="cookie-cat-marketing"
              />

              <p className="text-white/40 text-[11px] pt-2 border-t border-white/5">
                See our{" "}
                <Link to="/legal/cookies" className="text-[#C5A059] hover:underline">Cookie Policy</Link>{" "}
                for the full list of cookies used.
              </p>
            </div>

            <div className="border-t border-white/5 p-4 flex flex-col sm:flex-row items-center justify-between gap-3 bg-[#0E0E0E]">
              <button
                type="button"
                onClick={rejectAll}
                className="px-4 py-2 text-sm w-full sm:w-auto rounded-sm border border-white/15 text-white hover:bg-white/5"
                data-testid="cookie-customize-reject-all"
              >
                Reject all
              </button>
              <div className="flex gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={saveCustom}
                  className="px-4 py-2 text-sm rounded-sm border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 flex-1 sm:flex-none"
                  data-testid="cookie-customize-save"
                >
                  Save preferences
                </button>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="px-4 py-2 text-sm rounded-sm bg-[#C5A059] text-black font-semibold hover:bg-[#d4b070] flex-1 sm:flex-none"
                  data-testid="cookie-customize-accept-all"
                >
                  Accept all
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CategoryRow({ title, description, checked, onChange, disabled = false, testId }) {
  return (
    <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-3" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium">{title}</p>
          <p className="text-white/50 text-xs mt-1 leading-relaxed">{description}</p>
        </div>
        <label
          className={`relative inline-flex items-center shrink-0 select-none ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          aria-disabled={disabled}
        >
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={(e) => onChange && onChange(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-10 h-5 bg-[#222] peer-checked:bg-[#C5A059] rounded-full transition-colors" />
          <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
        </label>
      </div>
    </div>
  );
}
